import { Fragment, type ReactNode, useEffect, useMemo, useRef } from 'react';
import logError from '@alltrails/core/utils/logError';
import { type PageUI, type PageUISection, sectionKeys } from '../../../utils/PageUI/pageUI';
import PageUICommunityInsight from '../PageUICommunityInsight/PageUICommunityInsight';
import PageUIDivider from '../PageUIDivider/PageUIDivider';
import PageUISummary from '../PageUISummary/PageUISummary';
import PageUITrailResult from '../PageUITrailResult/PageUITrailResult';

// One section type, one component. Adding a section type means adding a case here.
const renderSection = (section: PageUISection, index: number): ReactNode => {
  switch (section.sectionType) {
    case 'llm-summary':
    case 'llm-error-summary':
      return <PageUISummary summary={section.summary} />;
    case 'trail-result':
      return <PageUITrailResult index={index} section={section} />;
    case 'community-insight':
      return <PageUICommunityInsight section={section} />;
    case 'divider':
      return <PageUIDivider />;
    default:
      // An unknown/un-supported section was added to the server contract, render nothing & log the unknonwn section type
      return null;
  }
};

const logUnknownSection = (sectionType: string) => {
  logError(new Error(`Unknown SDUI section type: ${sectionType}`));
};

type Props = {
  page: PageUI;
  /** Called once per unknown section type per page. Defaults to logging the error. */
  onUnknownSection?: (sectionType: string) => void;
};

/**
 * Renders a page's sections in the order the server sent them. Unknown section types render nothing.
 */
export default function PageUISectionList({ page, onUnknownSection = logUnknownSection }: Props) {
  const reported = useRef<{ page: PageUI; sectionTypes: Set<string> }>(undefined);

  const { nodes, unknownSectionTypes } = useMemo(() => {
    const unknownSectionTypes = new Set<string>();
    const nodes: ReactNode[] = [];
    const keys = sectionKeys(page.sections);

    page.sections.forEach((section, index) => {
      const node = renderSection(section, index);

      if (node) {
        nodes.push(<Fragment key={keys[index]}>{node}</Fragment>);
      } else {
        unknownSectionTypes.add(section.sectionType);
      }
    });

    return { nodes, unknownSectionTypes };
  }, [page]);

  useEffect(() => {
    if (reported.current?.page !== page) {
      reported.current = { page, sectionTypes: new Set() };
    }

    const { sectionTypes } = reported.current;

    unknownSectionTypes.forEach(sectionType => {
      if (!sectionTypes.has(sectionType)) {
        sectionTypes.add(sectionType);
        onUnknownSection(sectionType);
      }
    });
  }, [onUnknownSection, page, unknownSectionTypes]);

  return <>{nodes}</>;
}
